import { debug } from 'application/Shared/Utils';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { fields as formFields } from './FieldsForm';
import { fields as FieldsSummary } from './FieldsSummary';
import { type FactoryFunction } from './types';
import { useConditionalProperties } from './useConditionalProperties';

/**
 * A factory component that renders form fields based on a provided schema.
 *
 * @component
 * @param {Object} props - The component props
 * @param {FieldSchemaJson} props.schema - The schema object that defines the field properties
 * @param {string} [props.schema.control_type='text'] - The type of control to render
 * @param {('form'|'summary')} [props.variant='form'] - Determines which set of fields to use.
 * Uses formFields when set to 'form' and FieldsSummary when set to 'summary'
 * @returns {React.ReactElement|null} Returns the appropriate form field component or null if the field type is not supported
 *
 * @example
 * const schema = {
 *   control_type: 'text',
 *   // other schema properties
 * };
 *
 * <FieldFactory schema={schema} variant="form" />
 *
 * @throws {Error} Logs an error to console if the control_type is not supported
 */
export const FieldFactory: FactoryFunction = ({ schema, variant = 'form' }) => {
    const { control_type: workatoControl, control: auvikControl } = schema;
    const { t } = useTranslation();
    const fields = variant === 'form' ? formFields : FieldsSummary;

    // We stabilize the factory function to prevent unnecessary re-renders
    const Factory = useCallback<FactoryFunction>(
        ({ schema }) => FieldFactory({ schema, variant }),
        [variant]
    );

    const { shouldRender, control } = useConditionalProperties(auvikControl);

    // Don't render if shouldRender is false
    if (!shouldRender) {
        return null;
    }

    if (!('name' in schema)) {
        debug.error('Field name is required.', schema);
        return null;
    }

    // If our control is defined we want to use it. We default to text if no control type is provided.
    const controlType = auvikControl?.type || workatoControl || 'text';

    // Our `text` control type will define a format that will be used to determine control type if provided.
    const { format } = auvikControl || {};

    if (!(controlType in fields)) {
        debug.error(`Field type ${controlType} not supported`);
        return null;
    }

    if (format && !((format as string) in fields)) {
        debug.error(`Field type text and format ${format} are not supported`);
        return null;
    }

    const { Component, Transformer } =
        fields[(format || controlType) as unknown as keyof typeof fields];

    return (
        <Component
            Factory={Factory}
            sx={{ flex: 1 }}
            {...{
                label: '',
                ...Transformer(
                    {
                        ...schema,
                        // If we have a control object we want to pass it down to the transformer
                        ...(control && { control })
                    },
                    t
                )
            }}
        />
    );
};
